<script lang="ts">
  import { onDestroy, onMount, untrack, type Snippet } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { mdiPlus } from "@mdi/js"
  import {
    createPropertyUnit,
    updatePropertyUnit,
  } from "../../../../../../../../../../../../../../api/kehrblatt/unit-registry/property-units"
  import QuickUnitOverlayActions from "./_modules/quick-unit-overlay-actions"
  import type {
    QuickPropertyUnitRow,
    QuickPropertyUnitSaveResult,
  } from "../../overlays/propertyUnitsQuickEdit/contract"
  import {
    buildQuickUnitSavePlan,
    createBlankUnitRow,
    quickUnitRowValidationError,
    quickUnitRowsHaveChanges,
    quickUnitRowsHaveValidationErrors,
    residentialUnitRowsForProperty,
  } from "./_logic/unitRows"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Props = {
    property: Property
    selectedUnitIds?: string[]
    onSaved?: (result: QuickPropertyUnitSaveResult) => void | Promise<void>
  }

  let {
    property,
    selectedUnitIds = [],
    onSaved = async () => {},
    onremoveOverlay = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  let rows = $state<QuickPropertyUnitRow[]>(untrack(() => residentialUnitRowsForProperty(property)))
  let submitting = $state(false)
  let validationMessage = $state<string | null>(null)

  const saveDisabled = $derived(
    submitting || !quickUnitRowsHaveChanges(rows) || quickUnitRowsHaveValidationErrors(rows),
  )

  const inputValue = (value: unknown) => {
    if (value instanceof Event) {
      const target = value.target as HTMLInputElement | HTMLTextAreaElement | null

      return `${target?.value ?? ""}`
    }

    return inputValue(value)
  }

  const close = () => {
    if (submitting) return

    onremoveOverlay?.()
  }

  const addRow = () => {
    rows = [...rows, createBlankUnitRow()]
    validationMessage = null
  }

  const updateRow = (clientId: string, patch: Partial<QuickPropertyUnitRow>) => {
    rows = rows.map((row) => (row.clientId === clientId ? { ...row, ...patch } : row))
    validationMessage = null
  }

  const buildUpdatedProperty = (
    createdUnits: PropertyUnit[],
    updatedUnits: PropertyUnit[],
  ): Property => {
    const updatedById = new Map(updatedUnits.map((unit) => [unit.id, unit]))
    const existingUnits = (property.units ?? []).map((unit) => updatedById.get(unit.id) ?? unit)

    return { ...property, units: [...existingUnits, ...createdUnits] }
  }

  const save = async () => {
    if (submitting) return

    const firstInvalid = rows.map(quickUnitRowValidationError).find(Boolean)

    if (firstInvalid) {
      validationMessage = firstInvalid

      return
    }

    const plan = buildQuickUnitSavePlan({ propertyId: property.id, rows })

    if (!plan.creates.length && !plan.updates.length) {
      close()

      return
    }

    submitting = true
    try {
      const updatedUnits = await Promise.all(
        plan.updates.map((entry) => updatePropertyUnit(entry.id, entry.payload)),
      )
      const createdUnits = await Promise.all(
        plan.creates.map((entry) => createPropertyUnit(entry.payload)),
      )
      const nextProperty = buildUpdatedProperty(createdUnits, updatedUnits)
      const nextSelectedUnitIds = Array.from(
        new Set([...selectedUnitIds, ...createdUnits.map((unit) => unit.id).filter(Boolean)]),
      )
      await onSaved({
        property: nextProperty,
        selectedUnitIds: nextSelectedUnitIds,
        createdUnits,
        updatedUnits,
      })
      newNotification({ class: "success", html: "Wohneinheiten gespeichert." })
      onremoveOverlay?.()
    } catch (err) {
      console.error("batch_quick_property_units_save_failed", err)
      newNotification({ class: "error", html: "Wohneinheiten konnten nicht gespeichert werden." })
    } finally {
      submitting = false
    }
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(actionButtons)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })
</script>

<div class="quick-unit-overlay form">
  <p class="hint">
    Erfassen Sie hier nur Wohneinheiten dieser Liegenschaft. Bestehende Einheitsnummern bleiben
    unverändert; Einheiten werden in diesem Schnellschritt nicht gelöscht.
  </p>

  <div class="unit-list">
    {#each rows as row (row.clientId)}
      <ActionBox>
        <div class="row">
          <Input
            type="text"
            label="Einheitsnummer"
            placeholder="z. B. 1. OG links"
            value={row.unitNumber}
            disabled={submitting || !row.isNew}
            onChange={(value) => updateRow(row.clientId, { unitNumber: inputValue(value) })}
          />
          <Input
            type="text"
            label="Einheitenname"
            placeholder={row.unitNumber || "z. B. Wohneinheit Müller"}
            value={row.unitLabel}
            disabled={submitting}
            onChange={(value) => updateRow(row.clientId, { unitLabel: inputValue(value) })}
          />
        </div>
      </ActionBox>
    {/each}
  </div>

  {#if validationMessage}
    <p class="validation-message">{validationMessage}</p>
  {/if}

  <CTA
    type="secondaryButton"
    content="Wohneinheit hinzufügen"
    onClick={addRow}
    disabled={submitting}
  >
    {#snippet iconSnippet()}
      <Icon path={mdiPlus} />
    {/snippet}
  </CTA>
</div>

{#snippet actionButtons()}
  <QuickUnitOverlayActions {submitting} {saveDisabled} onCancel={close} onSave={save} />
{/snippet}

<style lang="less">
  .quick-unit-overlay {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .hint {
    margin: 0;
    color: color-mix(in srgb, var(--text-invers-100) 72%, transparent);
    line-height: 1.4;
  }

  .unit-list {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }

  .validation-message {
    margin: -0.2rem 0 0;
    color: var(--error-100, #b42318);
    font-weight: 700;
  }

  @media (max-width: 640px) {
    .quick-unit-overlay {
      min-width: 0;
    }
  }
</style>
